import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Package, Pencil, Search, Trash2 } from 'lucide-react'

import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useDebouncedCallback } from '@/hooks/use-debounced-callback'
import {
    SearchProductOutputItem,
    useProduct,
    useSearchProducts,
} from '@/lib/api/products'
import { cn } from '@/lib/utils'

interface SearchProductSingleVariantInputProps {
    storeId: string
    productId?: string
    variantId?: string
    onChange: (selection: { productId: string; variantId: string }) => void
    onClear?: () => void
    placeholder?: string
    includeInactive?: boolean
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
    const defaultVariant = product.variants.find((v) => v.default)
    return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null
}

export const SearchProductSingleVariantInput = ({
    storeId,
    productId,
    variantId,
    onChange,
    onClear,
    placeholder,
    includeInactive = false,
}: SearchProductSingleVariantInputProps) => {
    const { t } = useTranslation('common')
    const [open, setOpen] = React.useState(false)
    const [searchInput, setSearchInput] = React.useState('')
    const [searchValue, setSearchValue] = React.useState<string | null>(null)
    const [draftProductId, setDraftProductId] = React.useState<string | undefined>(productId)
    const [draftVariantId, setDraftVariantId] = React.useState<string | undefined>(variantId)

    const debouncedSetSearch = useDebouncedCallback((value: string) => {
        setSearchValue(value.length > 0 ? value : null)
    }, 250)

    React.useEffect(() => {
        if (open) {
            setDraftProductId(productId)
            setDraftVariantId(variantId)
            setSearchInput('')
            setSearchValue(null)
        }
    }, [open, productId, variantId])

    const { data: selectedProduct } = useProduct(productId ?? '', !!productId)
    const { data: searchResults, isLoading } = useSearchProducts(
        { storeId, productId: null, searchValue, includeInactive },
        { enabled: open },
    )

    const handlePickProduct = (product: SearchProductOutputItem) => {
        if (draftProductId === product.id) return
        setDraftProductId(product.id)
        setDraftVariantId(undefined)
    }

    const handlePickVariant = (id: string) => {
        setDraftVariantId(id)
    }

    const handleConfirm = () => {
        if (!draftProductId || !draftVariantId) return
        onChange({ productId: draftProductId, variantId: draftVariantId })
        setOpen(false)
    }

    const selectedImage = selectedProduct
        ? (selectedProduct.variants.find((v) => v.default)?.imageUrl ?? selectedProduct.variants[0]?.imageUrl ?? null)
        : null
    const selectedVariant = selectedProduct?.variants.find((v) => v.id === variantId)

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {productId ? (
                <div className="flex items-center gap-3 rounded-md border p-2">
                    <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
                        {selectedImage ? (
                            <img src={selectedImage} alt={selectedProduct?.name ?? ''} className="size-full object-cover" />
                        ) : (
                            <Package className="text-muted-foreground size-5" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-medium">{selectedProduct?.name ?? '…'}</p>
                            {selectedProduct?.active === false && (
                                <Badge variant="secondary">{t('formComponents.inactive')}</Badge>
                            )}
                        </div>
                        {selectedVariant?.name && (
                            <p className="text-muted-foreground truncate text-xs">{selectedVariant.name}</p>
                        )}
                    </div>
                    <DialogTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            aria-label={t('edit')}
                        >
                            <Pencil className="size-3.5" />
                        </Button>
                    </DialogTrigger>
                    {onClear && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive size-7"
                            onClick={onClear}
                            aria-label={t('delete')}
                        >
                            <Trash2 className="size-3.5" />
                        </Button>
                    )}
                </div>
            ) : (
                <DialogTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'border-input bg-background ring-offset-background focus-visible:ring-ring',
                            'text-muted-foreground flex h-8 w-full items-center gap-2 rounded-md border px-3 text-sm',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                        )}
                    >
                        <Search className="size-3.5 shrink-0" />
                        <span className="truncate">{placeholder ?? t('formComponents.searchProduct')}</span>
                    </button>
                </DialogTrigger>
            )}
            <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-[600px]">
                <DialogHeader className="border-b p-4">
                    <DialogTitle>
                        {t('formComponents.searchProductVariantTitle')}
                    </DialogTitle>
                </DialogHeader>
                <div className="border-b p-4">
                    <div className="relative">
                        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                        <Input
                            autoFocus
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value)
                                debouncedSetSearch(e.target.value)
                            }}
                            placeholder={t('formComponents.searchProduct')}
                            className="pl-10"
                        />
                    </div>
                </div>
                <div className="min-h-[200px] flex-1 overflow-y-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="text-muted-foreground size-5 animate-spin" />
                        </div>
                    )}
                    {!isLoading && (!searchResults || searchResults.length === 0) && (
                        <p className="text-muted-foreground p-8 text-center text-sm">
                            {t('formComponents.noProductsFound')}
                        </p>
                    )}
                    {!isLoading &&
                        searchResults?.map((product) => (
                            <ProductVariantRow
                                key={product.id}
                                product={product}
                                isSelected={draftProductId === product.id}
                                selectedVariantId={draftProductId === product.id ? draftVariantId : undefined}
                                onPickProduct={() => handlePickProduct(product)}
                                onPickVariant={handlePickVariant}
                            />
                        ))}
                </div>
                <DialogFooter className="border-t p-4">
                    <Button variant="outline" onClick={() => setOpen(false)}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleConfirm} disabled={!draftProductId || !draftVariantId}>
                        {t('confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface ProductVariantRowProps {
    product: SearchProductOutputItem
    isSelected: boolean
    selectedVariantId?: string
    onPickProduct: () => void
    onPickVariant: (variantId: string) => void
}

const ProductVariantRow = ({
    product,
    isSelected,
    selectedVariantId,
    onPickProduct,
    onPickVariant,
}: ProductVariantRowProps) => {
    const { t } = useTranslation('common')
    const imageUrl = getProductImage(product)

    return (
        <div className={cn('border-b last:border-b-0', isSelected ? 'bg-accent/40' : 'hover:bg-accent/20')}>
            <button type="button" onClick={onPickProduct} className="flex w-full items-center gap-3 p-3 text-left">
                <span
                    className={cn(
                        'border-input size-4 shrink-0 rounded-full border',
                        isSelected && 'border-primary bg-primary',
                    )}
                />
                <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md">
                    {imageUrl ? (
                        <img src={imageUrl} alt={product.name} className="size-full object-cover" />
                    ) : (
                        <Package className="text-muted-foreground size-5" />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-medium">{product.name}</p>
                        {!product.active && (
                            <Badge variant="secondary">{t('formComponents.inactive')}</Badge>
                        )}
                    </div>
                    {product.description && (
                        <p className="text-muted-foreground truncate text-xs">{product.description}</p>
                    )}
                </div>
                <span className="text-muted-foreground text-xs">
                    {t('formComponents.variantsTotal', { count: product.variants.length })}
                </span>
            </button>
            {isSelected && product.variants.length > 0 && (
                <div className="space-y-1 pb-3 pl-12 pr-4">
                    <Label className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                        {t('formComponents.variant')}
                    </Label>
                    {product.variants.map((variant) => {
                        const variantSelected = selectedVariantId === variant.id
                        return (
                            <button
                                key={variant.id}
                                type="button"
                                onClick={() => onPickVariant(variant.id)}
                                className={cn(
                                    'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs',
                                    variantSelected
                                        ? 'border-primary bg-primary/10'
                                        : 'border-input hover:bg-accent/40',
                                )}
                            >
                                <span
                                    className={cn(
                                        'border-input size-3.5 shrink-0 rounded-full border',
                                        variantSelected && 'border-primary bg-primary',
                                    )}
                                />
                                <span className="truncate">{variant.name}</span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
